import { Injectable, Signal, signal } from '@angular/core';

/**
 * Describes the maintenance lock as reported by a blocked request.
 */
export interface MaintenanceLockMarker {
  /**
   * The end of the running maintenance window as UTC timestamp, if known.
   */
  readonly endUtc: string | null;
  /**
   * The client timestamp (epoch milliseconds) at which the blocked response was received.
   *
   * Nur eine Serverantwort, die jünger als dieser Zeitpunkt ist, darf die Sperre wieder aufheben.
   */
  readonly receivedAt: number;
}

/**
 * Signalisiert eine serverseitig erkannte Wartungssperre an die Oberfläche.
 *
 * Der Übernehmer des Interceptors setzt das Signal, die Wartungs-Feature-Schicht liest es.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceModeSignalService {
  private readonly _lockedMarker = signal<MaintenanceLockMarker | null>(null);

  /**
   * The marker of the last blocked request, `null` while no request has been blocked.
   */
  readonly lockedMarker: Signal<MaintenanceLockMarker | null> = this._lockedMarker.asReadonly();

  /**
   * Reports a request that was blocked by the maintenance lock.
   * @param endUtc The end of the running maintenance window as UTC timestamp, `null` when unknown.
   */
  markLocked(endUtc: string | null): void {
    this._lockedMarker.set({ endUtc, receivedAt: Date.now() });
  }

  /**
   * Clears the marker once the server reports that the lock is gone.
   */
  clearLocked(): void {
    this._lockedMarker.set(null);
  }
}
